import { Link, useLocation, useNavigate } from "react-router-dom";
import { getListeSecteur } from "../../data/params/secteur";
import { getListeStatut } from "../../data/params/statut";

import "./dossiers.css";
import { FolderOpenOutlined } from "@ant-design/icons";
import {
  Button,
  Col,
  Input,
  message,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
} from "antd";
import { Fragment, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import PageLabel from "../../components/common/PageLabel/PageLabel";
import { FormContainer } from "../../components/common/common.components";
import FormLabel from "../../components/common/Form/FormLabel";
import SearchRow from "../../components/common/SearchRow";
import TableTitle from "../../components/common/TableTitle";

import { faEye, faFileExport, faUser } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { HStack } from "@chakra-ui/react";

import * as XLSX from "xlsx";

const ExercicesTermine = () => {
  const navigate = useNavigate();
  const location = useLocation();

  /** ----------------------------
   * STATES
   -----------------------------*/
  const [statuts, setStatuts] = useState([]);
  const [secteurs, setSecteurs] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filtres de recherche
  const [paramsRecherche, setParamsRecherche] = useState({
    infosClient: "",
    secteur_id: "",
    statut_id: "",
  });

  // Dossiers clôturés (issus du state de la navigation)
  const [clientsDossiers, setClientsDossiers] = useState(
    location?.state?.dossiers.filter((item) => item?.isCloture !== false) || []
  );

  // Résultats affichés dans le tableau
  const [filtersItems, setFiltersItems] = useState([]);

  // Paramètres de la table (pagination)
  const [tableParams, setTableParams] = useState({
    pagination: { current: 1, pageSize: 10 },
  });

  const user = useSelector((state) => state.user);
  const isAdmin = user.libelleProfil === "Admin"; // peut être utilisé plus tard

  /** ----------------------------
   * USE EFFECTS
   -----------------------------*/
  useEffect(() => {
    fetchStatuts();
    fetchSecteurs();
  }, []);

  // Initialise la liste quand les dossiers changent
  useEffect(() => {
    if (clientsDossiers.length) {
      setFiltersItems(clientsDossiers);
    }
  }, [clientsDossiers]);

  /** ----------------------------
   * FETCH DATA
   -----------------------------*/
  const fetchSecteurs = async () => {
    setLoading(true);
    const res = await getListeSecteur();
    setSecteurs(res?.result || []);
    setLoading(false);
  };

  const fetchStatuts = async () => {
    setLoading(true);
    const res = await getListeStatut();
    setStatuts(res?.result || []);
    setLoading(false);
  };

  /** ----------------------------
   * EXPORT EXCEL
   -----------------------------*/
  const handleExport = () => {
    try {
      const csvData = filtersItems.map((item) => ({
        Sigle: item?.client?.sigle,
        Dénomination: item?.client?.denomination,
        "Statut juridique": item?.client?.StatusJuridique?.libelle,
        "Secteur d'activité": item?.client?.SecteurActivite?.libelle,
        "Numéro R.C.C.M": item?.client?.numero_rccm,
        "Numéro compte contribuable (CC)": item?.client?.numero_cc,
        "Centre impôts": item?.client?.centre_impots,
        "Numéro CNPS employeur": item?.client?.numero_cnps_employeur,
        "Numéro agrément": item?.client?.numero_agrement,
        "Adresse géographique": item?.client?.localisation,
        "Adresse postale": item?.client?.adresse_postale,
        "Téléphone 1": item?.client?.cel1,
        "Téléphone 2": item?.client?.cel2,
        Email: item?.client?.email,
        "Site internet": item?.client?.site_internet,
        "Nom du dirigeant": item?.client?.nom_dirigeant,
        "Dernière mission": item?.mission?.mission,
        "Année mission": item?.mission?.annee,
        "Mission clôturée": item?.mission?.isCloture ? "Oui" : "Non",
        "Nom du manager": `${item?.nom_manager || ""} ${
          item?.prenom_manager || ""
        }`.trim(),
      }));

      const worksheet = XLSX.utils.json_to_sheet(csvData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Dossiers");
      XLSX.writeFile(workbook, "liste_dossiers.xlsx");

      message.success("Export réussi");
    } catch (error) {
      console.error(error);
      message.error("Erreur lors de l'export");
    }
  };

  /** ----------------------------
   * RECHERCHE ET RESET
   -----------------------------*/
  const handleSearch = () => {
    setLoading(true);

    setTimeout(() => {
      const result = clientsDossiers.filter(
        (dossier) =>
          // Filtre texte (mission ou client)
          (dossier?.sigle_client
            ?.toLowerCase()
            .includes(paramsRecherche.infosClient.toLowerCase()) ||
            dossier?.mission_name
              ?.toLowerCase()
              .includes(paramsRecherche.infosClient.toLowerCase())) &&
          // Filtre secteur
          (paramsRecherche.secteur_id === "" ||
            dossier?.client?.secteur_id ===
              Number(paramsRecherche.secteur_id)) &&
          // Filtre statut
          (paramsRecherche.statut_id === "" ||
            dossier?.client?.statut_id === Number(paramsRecherche.statut_id))
      );

      setFiltersItems(result);
      setLoading(false);
    }, 1000);
  };

  const handleReset = () => {
    setParamsRecherche({ infosClient: "", secteur_id: "", statut_id: "" });
    setFiltersItems(clientsDossiers);
  };

  /** ----------------------------
   * COLONNES DU TABLEAU
   -----------------------------*/
  const exercicesUserColumns = [
    {
      title: <TableTitle>#</TableTitle>,
      width: 50,
      render: (_, __, index) => index + 1,
    },
    {
      title: <TableTitle>Exercice</TableTitle>,
      dataIndex: "exercice",
      key: "exercice",
      render: (_, record) => (
        <div className="d-flex align-items-center">
          <FolderOpenOutlined
            style={{ fontSize: "3rem", color: "rgb(250, 152, 28)" }}
          />
          <div className="ms-3">
            <Link
              to={`/clients/mission/exploitation/${record?.exercice_id}`}
              state={{ record: record?.client, mission: record?.mission }}
              className="text-gray-800 text-hover-primary"
            >
              <span className="fw-bolder fs-6">
                {record?.mission_name} - {record?.mission_annee}
              </span>
              <br />
              <span className="text-muted fs-9 text-uppercase">
                <Tag>{record?.sigle_client}</Tag> | {record?.status_client}
              </span>
            </Link>
          </div>
        </div>
      ),
    },
    {
      title: <TableTitle>Secteur juridique</TableTitle>,
      dataIndex: "secteur_client",
      render: (value) => (
        <span className="fw-bold text-uppercase">{value}</span>
      ),
    },
    {
      title: <TableTitle>Manager</TableTitle>,
      render: (_, record) => (
        <Space>
          <FontAwesomeIcon icon={faUser} className="text-warning fs-5" />
          <span className="fw-bolder text-uppercase">
            {record?.nom_manager} {record?.prenom_manager}
          </span>
        </Space>
      ),
    },
    {
      title: <TableTitle>Statut</TableTitle>,
      render: (_, record) =>
        !record?.isCloture ? (
          record?.isLettreMission === 0 && record?.etat === 1 ? (
            <Tag color="yellow">En attente</Tag>
          ) : (
            <Tag color="blue">En cours</Tag>
          )
        ) : (
          <Tag color="green">Clôturé</Tag>
        ),
    },
    {
      title: <TableTitle>Progression</TableTitle>,
      render: (_, record) => (
        <div>
          <span className="text-muted">
            {record?.isCloture ? 100 : record?.progression}%
          </span>
          <div className="progress h-6px w-100">
            <div
              className={`progress-bar bg-${record?.color}`}
              style={{
                width: `${record?.isCloture ? 100 : record?.progression}%`,
              }}
            ></div>
          </div>
        </div>
      ),
    },
    {
      title: <TableTitle>Actions</TableTitle>,
      align: "right",
      render: (_, record) => (
        <Tooltip title="Consulter le sommaire">
          <Button
            type="primary"
            icon={<FontAwesomeIcon icon={faEye} />}
            onClick={() =>
              navigate(`/clients/mission/exploitation/${record?.exercice_id}`, {
                state: { record: record?.client, mission: record?.mission },
              })
            }
          />
        </Tooltip>
      ),
    },
  ];

  /** ----------------------------
   * RENDER
   -----------------------------*/
  return (
    <Fragment>
      <HStack justifyContent="space-between" alignItems="center">
        <PageLabel label="Exercices terminés" />
      </HStack>

      {/* Barre de recherche */}
      <SearchRow
        handleSearch={handleSearch}
        handleReset={handleReset}
        loading={loading}
      >
        <Col span={8}>
          <FormLabel label="Mission/Client" />
          <Input
            size="large"
            placeholder="Mission ou client"
            value={paramsRecherche.infosClient}
            onChange={(e) =>
              setParamsRecherche({
                ...paramsRecherche,
                infosClient: e.target.value,
              })
            }
          />
        </Col>
        <Col span={8}>
          <FormLabel label="Statut juridique" />
          <Select
            size="large"
            style={{ width: "100%" }}
            placeholder="Statut juridique"
            value={paramsRecherche.statut_id}
            onChange={(value) =>
              setParamsRecherche({ ...paramsRecherche, statut_id: value })
            }
            options={statuts.map((s) => ({ value: s.id, label: s.libelle }))}
          />
        </Col>
        <Col span={8}>
          <FormLabel label="Secteur d'activité" />
          <Select
            size="large"
            style={{ width: "100%" }}
            placeholder="Secteur d'activité"
            value={paramsRecherche.secteur_id}
            onChange={(value) =>
              setParamsRecherche({ ...paramsRecherche, secteur_id: value })
            }
            options={
              secteurs.map((s) => ({ value: s.id, label: s.libelle })) || []
            }
          />
        </Col>
      </SearchRow>

      {/* Bouton Export */}
      <Space className="d-flex justify-content-end pt-4">
        <Button
          type="primary"
          icon={<FontAwesomeIcon icon={faFileExport} />}
          onClick={handleExport}
        >
          Exporter
        </Button>
      </Space>

      {/* Tableau des exercices */}
      <FormContainer>
        <Table
          columns={exercicesUserColumns}
          rowKey={(record) => record.id}
          dataSource={filtersItems}
          pagination={tableParams.pagination}
          loading={loading}
          onChange={(pagination) => setTableParams({ pagination })}
        />
      </FormContainer>
    </Fragment>
  );
};

export default ExercicesTermine;
